import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Dual-client support for the multi-tenant architecture
 * (SPEC_MULTI_TENANT_ISOLATION). The frontend talks to two databases:
 *
 *   * `supabase` — firm DB. All firm-scoped reads/writes (conversations,
 *     conversation_turns, matters, matter_documents) and all auth flows go
 *     here. Driven by VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY.
 *
 *   * `supabaseMain` — main DB. Read-only corpus lookups go here
 *     (chunks, cafc_opinions citation hydration in api.ts). Driven by
 *     VITE_MAIN_SUPABASE_URL / VITE_MAIN_SUPABASE_ANON_KEY when set,
 *     otherwise falls back to the firm DB so single-tenant deployments
 *     keep working.
 *
 * In a true multi-tenant deployment, set the firm-DB env vars to the
 * firm-specific Supabase project (acme.supabase.co) and the main-DB env
 * vars to the shared corpus project (corpus.supabase.co). For single-tenant
 * dev/staging, leave the MAIN env vars unset and a single client serves
 * both purposes.
 *
 * The frontend does not resolve firms — that happens at the edge function
 * layer. The frontend is told which firm-DB URL/anon-key to use at
 * build/deploy time (or runtime, via subdomain-driven environment
 * injection). This file only consumes the variables.
 */

// The local demo harness never sends requests to Supabase, but this module is
// imported before the router can inspect `?demo=1`. createClient throws on
// missing values, which previously made the advertised offline harness crash
// on a clean checkout. Safe inert values keep development/demo bootable while
// production builds still fail clearly when configuration is absent.
const firmUrl =
  import.meta.env.VITE_SUPABASE_URL
  || (import.meta.env.DEV ? 'http://127.0.0.1:54321' : '');
const firmAnon =
  import.meta.env.VITE_SUPABASE_ANON_KEY
  || (import.meta.env.DEV ? 'demo-placeholder' : '');

if (!firmUrl || !firmAnon) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. '
      + 'Configure both variables for production.',
  );
}

const mainUrl = import.meta.env.VITE_MAIN_SUPABASE_URL ?? firmUrl;
const mainAnon = import.meta.env.VITE_MAIN_SUPABASE_ANON_KEY ?? firmAnon;

export const supabase: SupabaseClient = createClient(firmUrl, firmAnon);

export const supabaseMain: SupabaseClient =
  mainUrl === firmUrl && mainAnon === firmAnon
    ? supabase
    : createClient(mainUrl, mainAnon);
